import { addMetadata, PopUpMessageContext, useApp } from '@hb/react'
import {
  capitalizeFirstLetter,
  FieldMapValue,
  getCoverageLabel,
  getCoveragePath,
  InsuranceCoverage,
  InsuranceCoverageRequest,
  mergeData,
  PatientCoverageId,
  PopulatedAssessment,
  PregnancyCoverageLabel,
  User,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { DocumentReference, updateDoc } from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import { useCallback, useContext } from 'react'
import { updateAssessmentAnswers } from '../../../utils/assessments/updateAssessmentAnswers'
import { getAssessmentCoverageDataPath } from './utils'

export const useSubmitCoverage = (
  patientRef: DocumentReference<User> | null | undefined,
  id: PatientCoverageId,
  coverage: InsuranceCoverage | null | undefined,
  assessmentId: string | null,
  assessment: PopulatedAssessment | null | undefined,
  pregnancyCoverageId: PregnancyCoverageLabel | null,
  request: InsuranceCoverageRequest | undefined | null,
) => {
  const { showMessage } = useContext(PopUpMessageContext)
  const { appName } = useApp()

  return useCallback(
    async (data: FieldMapValue) => {
      if (!patientRef) {
        showMessage({
          text: 'An error occurred',
          subText:
            'Please send us a message on the contact page and we will get back to you as soon as possible',
          type: 'error',
        })
        return { [FORM_ERROR]: 'An error occurred' }
      }
      if (assessmentId && !assessment) {
        showMessage({
          text: 'Assessment ID provided, but assessment not found',
          subText: 'Get in touch with Elan',
          type: 'error',
        })
        return {
          [FORM_ERROR]: 'Assessment ID provided, but assessment not found - get in touch with Elan',
        }
      }
      try {
        const withMetadata = addMetadata(data, appName, !coverage?.createdOn)
        if (request && !coverage?.fromRequest) withMetadata.fromRequest = request
        if (request?.withCallInForm) {
          withMetadata.callInRequests = {
            [Date.now()]: addMetadata(
              {
                on: Date.now(),
                by: 'admin',
                notes: `From coverage request | ${request.message}`,
              },
              appName,
              true,
            ),
            ...withMetadata.callInRequests,
          }
        }
        const assessmentCoverageDataPath = getAssessmentCoverageDataPath(pregnancyCoverageId)
        if (assessmentId && assessmentCoverageDataPath) {
          const submitted = {}
          nestedSet(submitted, assessmentCoverageDataPath, withMetadata)
          await updateAssessmentAnswers({
            id: assessmentId,
            data: submitted,
            appName,
          })
        } else {
          const propPath = getCoveragePath(id)
          await updateDoc(patientRef, propPath, mergeData(coverage ?? {}, withMetadata))
        }
        showMessage({
          type: 'success',
          text: `${capitalizeFirstLetter(getCoverageLabel(id, assessment))} coverage updated!`,
        })
      } catch (err: any) {
        showMessage({
          text: 'An error occurred',
          subText:
            err.message ||
            'Please send us a message on the contact page and we will get back to you as soon as possible',
          type: 'error',
        })
        return {
          [FORM_ERROR]:
            err.message ||
            'An error occurred. Please send us a message on the contact page and we will get back to you as soon as possible',
        }
      }
      return undefined
    },
    [
      patientRef,
      showMessage,
      coverage,
      appName,
      request,
      id,
      assessmentId,
      assessment,
      pregnancyCoverageId,
    ],
  )
}
